import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_URL } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, CalendarClock, Ban } from "lucide-react";
import Logo from "@/components/Logo";

async function pub(path, options = {}) {
  const res = await fetch(`${BACKEND_URL}/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try { const j = await res.json(); detail = j.detail || j.message || detail; } catch (_) {}
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return res.json();
}

export default function BookingManage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);   // 'cancel' | 'reschedule'
  const [reason, setReason] = useState("");
  const [newStart, setNewStart] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    pub(`/public/booking/manage/${token}`).then(setData).catch((e) => setError(e.message));
  }, [token]);

  const cancel = async () => {
    try {
      const r = await pub(`/public/booking/manage/${token}/cancel`, {
        method: "POST", body: JSON.stringify({ reason }),
      });
      setDone("cancelled");
      // Success level chosen from the backend `external_sync` field, not
      // from HTTP 200 alone. Never expose provider names raw to the client;
      // roll them up into a single readable warning.
      const sync = r?.external_sync;
      if (sync === "partial") {
        const failed = Object.entries(r?.sync_detail || {})
          .filter(([, v]) => v === "failed")
          .map(([k]) => k.charAt(0).toUpperCase() + k.slice(1));
        toast.warning(
          `Booking cancelled — but ${failed.join(" and ") || "calendar"} could not be updated. The provider will sync it manually.`,
          { duration: 8000 }
        );
      } else if (sync === "complete") {
        toast.success("Booking cancelled. Calendar and client notifications updated.");
      } else {
        // no external systems involved
        toast.success("Booking cancelled.");
      }
    } catch (e) {
      const msg = (e && e.message) || "";
      toast.error(msg.toLowerCase().includes("past_cancel_deadline")
        ? "This booking can no longer be cancelled online. Please contact the provider."
        : "Cancellation failed. No changes were made.");
    }
  };
  const reschedule = async () => {
    if (!newStart) return toast.error("Pick a time");
    try { await pub(`/public/booking/manage/${token}/reschedule`, {
      method: "POST", body: JSON.stringify({ starts_at: new Date(newStart).toISOString() })
    }); setDone("rescheduled"); toast.success("Rescheduled"); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-[#2C3A29] text-[#FAF8F5]">
        <div className="max-w-2xl mx-auto px-6 py-6 flex items-center justify-between">
          <Logo variant="full" size={36} onDark />
          <div className="text-xs text-[#A8A29E]">Manage your booking</div>
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {error && <p className="text-sm text-[#B02222]">{error}</p>}
        {done && (
          <div className="bg-white rounded-3xl border border-[#E8E2D9] p-8 text-center" data-testid="manage-done">
            <Check size={28} className="mx-auto text-[#2C3A29] mb-2" />
            <h2 className="font-display font-bold text-xl">Your booking is {done}</h2>
            <p className="text-xs text-[#A8A29E] mt-2">You should receive a confirmation email shortly.</p>
          </div>
        )}
        {!done && data && (
          <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8" data-testid="manage-view">
            <h2 className="font-display font-bold text-xl">{data.booking.appointment_name || "Booking"}</h2>
            <div className="text-sm text-[#57534E] mt-1">{data.org?.name}</div>
            <div className="mt-3 text-sm">
              <div><b>When:</b> {new Date(data.booking.starts_at).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
              <div className="text-xs text-[#A8A29E]">{data.booking.timezone}</div>
              <div><b>Duration:</b> {data.booking.duration_minutes} min</div>
              <div><b>Status:</b> <span className="capitalize">{data.booking.status}</span></div>
              {data.booking.meeting_url && <div className="mt-2"><a href={data.booking.meeting_url} target="_blank" rel="noreferrer" className="text-[#E8712C]">Join meeting →</a></div>}
            </div>

            {data.booking.status === "confirmed" && (
              <div className="mt-6 flex flex-wrap gap-2">
                {data.can_reschedule && (
                  <Button variant="outline" onClick={() => setMode("reschedule")} data-testid="manage-reschedule-btn"
                          className="rounded-full"><CalendarClock size={13} className="mr-1" /> Reschedule</Button>
                )}
                {data.can_cancel && (
                  <Button variant="outline" onClick={() => setMode("cancel")} data-testid="manage-cancel-btn"
                          className="rounded-full"><Ban size={13} className="mr-1" /> Cancel</Button>
                )}
              </div>
            )}

            {mode === "cancel" && (
              <div className="mt-5 p-3 border border-[#E8E2D9] rounded-2xl space-y-2">
                <div className="text-sm font-medium">Cancel this appointment?</div>
                <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)}
                       data-testid="manage-reason" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMode(null)} className="rounded-full">Keep</Button>
                  <Button onClick={cancel} className="rounded-full bg-[#B02222] hover:bg-[#8f1c1c] text-white"
                          data-testid="manage-cancel-confirm">Cancel booking</Button>
                </div>
              </div>
            )}

            {mode === "reschedule" && (
              <div className="mt-5 p-3 border border-[#E8E2D9] rounded-2xl space-y-2">
                <div className="text-sm font-medium">Pick a new time</div>
                <Input type="datetime-local" value={newStart} onChange={(e) => setNewStart(e.target.value)}
                       data-testid="manage-new-time" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMode(null)} className="rounded-full">Back</Button>
                  <Button onClick={reschedule} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white"
                          data-testid="manage-reschedule-confirm">Reschedule</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
